import {
  type CustomerOrder,
  PRODUCT_TURNAROUNDS,
  type Project,
  type ProjectFile,
  type ScriptAnalysis,
  type ScriptProject,
  scriptDraftOptionsSchema,
  type Workspace,
} from '@noconflict/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOrderReferenceRequired, useProjectOrder, useScriptQuote } from '../../entities/order';
import { useOrgContext } from '../../entities/org';
import { useScriptFile } from '../../entities/project-file';
import { useScriptAnalysis } from '../../entities/script-analysis';
import { useScriptProject } from '../../entities/script-project';
import { useDeliveryEstimates } from '../../features/order/delivery-estimate';
import { DiscardDraftButton } from '../../features/order/discard-draft';
import {
  PriceSummary,
  type SummaryPrice,
  type SummaryRow,
} from '../../features/order/price-summary';
import { ScriptUploadNotices } from '../../features/order/privacy-notice';
import { ScriptActions } from '../../features/order/replace-script';
import { ScriptOrderForm, useScriptDraft } from '../../features/order/script-order-form';
import { UploadScript } from '../../features/order/upload-script';
import { useFormat } from '../../shared/i18n/format';
import { ErrorState } from '../../shared/ui/error-state';
import { Skeleton } from '../../shared/ui/skeleton';
import { SubmitOrder } from '../../widgets/order-panel';
import { VersionsCard } from '../../widgets/versions-card';
import { type DetailRow, OrderDetails } from './order-details';

/** "Medium length · 18,776 words", as the form and the read-only view both say it. */
function useLengthText() {
  const { t } = useTranslation('order');
  const { formatNumber } = useFormat();
  return (analysis: ScriptAnalysis | null): string | null =>
    analysis?.length
      ? t('script.length.summary', {
          band: t(`script.length.bands.${analysis.length}`),
          words: t('script.length.words', {
            count: analysis.word_count ?? 0,
            formatted: formatNumber(analysis.word_count ?? 0),
          }),
        })
      : null;
}

/**
 * A script clearance project. While it is a draft, and for someone who may order and edit here, it starts with
 * the upload (beside what the customer is told about privacy), and once the script has been read it is the order
 * form with the price summary beside it. From submit on it is read-only, like a title project, and once ordered the
 * Versions card follows.
 */
export function ScriptProjectView({
  project,
  workspace,
}: {
  project: Project;
  workspace: Workspace;
}) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { org, canIn } = useOrgContext();
  const details = useScriptProject(project.id);
  const order = useProjectOrder(project.id);
  const analysis = useScriptAnalysis(project.id);
  const file = useScriptFile(project.id);
  const referenceRequired = useOrderReferenceRequired(org.id);
  const archived = workspace.archived_at !== null;
  const canOrder = canIn(workspace.id, 'orders.create');
  const canEdit = canIn(workspace.id, 'projects.edit');

  const failed = [details, order, analysis, file].find((query) => query.isError);
  if (failed) {
    return (
      <ErrorState
        title={t('form.error')}
        description={failed.error?.message}
        onRetry={() => {
          for (const query of [details, order, analysis, file]) void query.refetch();
        }}
      />
    );
  }
  if (
    details.isPending ||
    order.isPending ||
    analysis.isPending ||
    file.isPending ||
    referenceRequired.isPending ||
    !details.data
  ) {
    return (
      <section aria-busy="true" aria-label={t('form.loading')} className="grid gap-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </section>
    );
  }

  const o = order.data;
  const ready = analysis.data?.status === 'ready' ? analysis.data : null;
  const editable = o?.status === 'draft' && project.status === 'draft';
  if (editable && canOrder && canEdit && !archived) {
    if (!ready) {
      return (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <div className="grid content-start gap-4">
            <UploadScript projectId={project.id} />
            <div className="flex justify-end">
              <DiscardDraftButton
                projectId={project.id}
                name={project.name}
                label={t('form.discard')}
                onDiscarded={() =>
                  navigate(`/o/${org.slug}/w/${workspace.slug}`, { replace: true })
                }
              />
            </div>
          </div>
          <ScriptUploadNotices />
        </div>
      );
    }
    return (
      <ScriptDraftEditor
        // a different script is a different form: it is filled in again from the new file
        key={ready.id}
        project={project}
        workspace={workspace}
        details={details.data}
        order={o}
        analysis={ready}
        file={file.data ?? null}
        referenceRequired={referenceRequired.data === true}
      />
    );
  }
  const readOnly = (
    <ScriptProjectDetails
      project={project}
      workspace={workspace}
      details={details.data}
      order={o ?? null}
      analysis={analysis.data ?? null}
      file={file.data ?? null}
      notice={editable ? (archived ? t('form.archived') : t('form.readOnly')) : null}
    />
  );
  if (project.status === 'draft') return readOnly;
  // once ordered: the versions, for anyone who can read the project
  return (
    <div className="grid gap-6">
      {readOnly}
      <VersionsCard
        projectId={project.id}
        projectStatus={project.status}
        canEdit={canEdit && !archived}
      />
    </div>
  );
}

/** The editable draft once the script has been read: form, live price, delivery estimate, submit and discard. */
function ScriptDraftEditor({
  project,
  workspace,
  details,
  order,
  analysis,
  file,
  referenceRequired,
}: {
  project: Project;
  workspace: Workspace;
  details: ScriptProject;
  order: CustomerOrder;
  analysis: ScriptAnalysis;
  file: ProjectFile | null;
  referenceRequired: boolean;
}) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { org } = useOrgContext();
  const { formatCurrency } = useFormat();
  const lengthText = useLengthText();
  const draft = useScriptDraft({ projectId: project.id, details, order, referenceRequired });
  const estimates = useDeliveryEstimates(PRODUCT_TURNAROUNDS.script);
  const { scope, turnaroundDays, additionalClearances, versions, title } = draft.values;
  // The length comes from the analysis and the scope is not priced, so there is always a price to show.
  const quoteOptions = useMemo(
    () => ({ turnaroundDays, additionalClearances, versions }),
    [turnaroundDays, additionalClearances, versions],
  );
  const quote = useScriptQuote(org.id, project.id, analysis.id, quoteOptions);
  const price: SummaryPrice = quote.isError
    ? { state: 'error' }
    : quote.data
      ? { state: 'ready', quote: quote.data }
      : { state: 'loading' };
  const amount =
    quote.data && 'amountCents' in quote.data
      ? formatCurrency(quote.data.amountCents, quote.data.currency)
      : null;
  const estimate = estimates.get(turnaroundDays);
  const rows: SummaryRow[] = [
    { label: t('review.report'), value: t('products.script'), wideOnly: true },
    { label: t('review.name'), value: title.trim() || t('drafts.untitled'), wideOnly: true },
    { label: t('review.length'), value: lengthText(analysis) },
    { label: t('review.scope'), value: scope ? t(`scope.${scope}`) : null },
    { label: t('review.turnaround'), value: t('turnaround', { count: turnaroundDays }) },
    {
      label: t('review.clearances'),
      value: t('script.form.clearanceTitle', { count: additionalClearances }),
      wideOnly: true,
    },
    {
      label: t('review.revisions'),
      value:
        versions === 0
          ? t('script.form.revisionNone')
          : t('script.form.revisionTitle', { count: versions }),
      wideOnly: true,
    },
    ...(estimate
      ? [{ label: t('review.delivery'), value: estimate.deliveredBy, wideOnly: true }]
      : []),
  ];

  return (
    // The bottom padding keeps the last field clear of the summary bar on a phone.
    <div className="grid gap-6 pb-48 lg:grid-cols-[minmax(0,1fr)_20rem] lg:pb-0">
      <div className="grid gap-4">
        <ScriptOrderForm
          draft={draft}
          analysis={analysis}
          filename={file?.original_filename ?? null}
          scriptActions={<ScriptActions projectId={project.id} before={draft.flush} />}
          estimates={estimates}
          referenceRequired={referenceRequired}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p
            role={draft.status === 'error' ? 'alert' : 'status'}
            className={
              draft.status === 'error'
                ? 'text-sm text-destructive'
                : 'text-sm text-muted-foreground'
            }
          >
            {draft.status === 'saving'
              ? t('form.saving')
              : draft.status === 'saved'
                ? t('form.saved')
                : draft.status === 'error'
                  ? `${t('form.saveError')} ${draft.error ?? ''}`
                  : null}
          </p>
          <DiscardDraftButton
            projectId={project.id}
            name={title.trim() || t('drafts.untitled')}
            label={t('form.discard')}
            onDiscarded={() => navigate(`/o/${org.slug}/w/${workspace.slug}`, { replace: true })}
          />
        </div>
      </div>
      <PriceSummary
        rows={rows}
        price={price}
        missing={draft.missing.map((field) => t(`review.missing.${field}`))}
        action={
          <SubmitOrder
            order={order}
            workspaceId={workspace.id}
            amount={amount}
            disabled={draft.missing.length > 0}
            before={draft.flush}
          />
        }
      />
    </div>
  );
}

/** From submit on, and for anyone who may not order here: the details and choices, read-only, and the status. */
function ScriptProjectDetails({
  project,
  workspace,
  details,
  order,
  analysis,
  file,
  notice,
}: {
  project: Project;
  workspace: Workspace;
  details: ScriptProject;
  order: CustomerOrder | null;
  analysis: ScriptAnalysis | null;
  file: ProjectFile | null;
  notice: string | null;
}) {
  const { t, i18n } = useTranslation('order');
  const lengthText = useLengthText();
  const options = scriptDraftOptionsSchema.safeParse(order?.options).data;
  const list = new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' });
  const countryNames = new Intl.DisplayNames([i18n.language], { type: 'region' });
  const episode = [details.episode_number, details.episode_title].filter(Boolean).join(' · ');
  const optional = (label: string, value: string | null | undefined): DetailRow[] =>
    value ? [{ label, value }] : [];
  const rows: DetailRow[] = [
    { label: t('review.report'), value: t('products.script') },
    { label: t('review.name'), value: details.title ?? t('review.notEntered') },
    ...optional(t('review.episode'), episode),
    {
      label: t('review.productionType'),
      value: details.production_type
        ? t(`form.productionTypes.${details.production_type}`)
        : t('review.notChosen'),
    },
    ...optional(t('review.producer'), details.producer),
    ...optional(t('review.authors'), details.authors.length ? list.format(details.authors) : null),
    ...optional(t('review.script'), file?.original_filename),
    ...optional(t('review.length'), lengthText(analysis)),
    ...optional(
      t('review.timePeriods'),
      details.time_periods.length
        ? list.format(details.time_periods.map((period) => t(`script.form.periods.${period}`)))
        : null,
    ),
    ...optional(
      t('review.countries'),
      details.no_specific_country
        ? t('script.form.noSpecificCountry')
        : details.countries.length
          ? list.format(details.countries.map((code) => countryNames.of(code) ?? code))
          : null,
    ),
    ...optional(
      t('review.cities'),
      details.cities.length
        ? list.format(
            details.cities.map(
              (city) => `${city.name} (${countryNames.of(city.country) ?? city.country})`,
            ),
          )
        : null,
    ),
    ...optional(
      t('review.basedOnReal'),
      details.based_on_real === null
        ? null
        : [details.based_on_real ? t('review.yes') : t('review.no'), details.based_on_real_note]
            .filter(Boolean)
            .join('\n'),
    ),
    ...(options
      ? [
          {
            label: t('review.scope'),
            value: options.scope ? t(`scope.${options.scope}`) : t('review.notChosen'),
          },
          {
            label: t('review.turnaround'),
            value: t('turnaround', { count: options.turnaroundDays }),
          },
          {
            label: t('review.clearances'),
            value: t('script.form.clearanceTitle', { count: options.additionalClearances }),
          },
          {
            label: t('review.revisions'),
            value:
              options.versions === 0
                ? t('script.form.revisionNone')
                : t('script.form.revisionTitle', { count: options.versions }),
          },
        ]
      : []),
    ...optional(t('review.reference'), order?.reference),
    ...optional(t('review.notes'), details.notes),
  ];

  return (
    <OrderDetails
      project={project}
      workspace={workspace}
      order={order}
      notice={notice}
      rows={rows}
      wideLabels={[t('review.notes'), t('review.basedOnReal')]}
    />
  );
}
